import { FileStackIcon, NotebookPenIcon } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';

import { FormField, FormInput } from '@/_components';
import { CardContainer, FormFilesInput, FormLayout, SectionHeading } from '@/_containers';
import { RichText } from '@/_containers/inputs/rich-text';
import { allowedFileTypes, showFormField } from '@/_shared';

import { AddressSection } from './address-section';

import type { GovernanceFormSchema } from '../schemas';

export function FormFields({
  id,
  form,
  isEditable = true,
  onRequestEdit = () => {},
  autoFocus = false,
}: {
  id: string;
  form: UseFormReturn<GovernanceFormSchema>;
  isEditable?: boolean;
  onRequestEdit?: () => void;
  autoFocus?: boolean;
}) {
  const folder_path = `governance/${id}`;
  const [notes, supporting_documentation] = form.watch(['notes', 'supporting_documentation']);

  return (
    <>
      <CardContainer>
        <FormLayout>
          <FormField
            control={form.control}
            name="legal_business_name"
            render={({ field: { value, onChange } }) => (
              <FormInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Legal Business Name (required)"
                value={value}
                onChange={onChange}
                disabled={!isEditable}
                autoFocus={autoFocus}
              />
            )}
          />

          <FormField
            control={form.control}
            name="jurisdiction"
            render={({ field: { value, onChange } }) => (
              <FormInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Jurisdiction"
                value={value}
                onChange={onChange}
                disabled={!isEditable}
              />
            )}
          />

          <FormField
            control={form.control}
            name="registration_number"
            render={({ field: { value, onChange } }) => (
              <FormInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Registration Number"
                value={value}
                onChange={onChange}
                disabled={!isEditable}
              />
            )}
          />

          <FormField
            control={form.control}
            name="registered_agent_name"
            render={({ field: { value, onChange } }) => (
              <FormInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Registered Agent Name"
                value={value}
                onChange={onChange}
                disabled={!isEditable}
              />
            )}
          />
        </FormLayout>
      </CardContainer>

      <AddressSection isEdit={isEditable} onRequestEdit={onRequestEdit} />

      {showFormField(isEditable, (supporting_documentation?.length ?? []) > 0) && (
        <CardContainer>
          <SectionHeading text="Documents" icon={FileStackIcon} />

          <FormField
            control={form.control}
            name="supporting_documentation"
            render={({ field: { value, onChange } }) => (
              <FormFilesInput
                folder_path={folder_path}
                value={value}
                disabled={!isEditable}
                onChange={onChange}
                formItemClassName="mt-6"
                allowedTypes={allowedFileTypes.all}
              />
            )}
          />
        </CardContainer>
      )}

      {showFormField(isEditable, notes.trim()) && (
        <CardContainer>
          <SectionHeading text="Notes" icon={NotebookPenIcon} />
          <FormLayout className="mt-8">
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <RichText
                  value={field.value}
                  onChange={field.onChange}
                  disabled={!isEditable}
                  inline={!isEditable}
                  mediaFolderPath={folder_path}
                  className="col-span-12"
                />
              )}
            />
          </FormLayout>
        </CardContainer>
      )}
    </>
  );
}
